import { useCallback, useEffect, useMemo, useState } from 'react';

import { usePathname, useSearchParams } from 'next/navigation';

import { OtpData, useOtp } from '@/api/useOtp';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { ELoginMethods, EOTPTextModel, EPlatform, EPlatformType } from '@/lib/enums';
import { getButtonStyle, getQrCodeForegroundColor, getUtmParams, QR_CODE_BACKGROUND_COLOR } from '@/lib/utils';
import { useAppState } from '@/state/hooks/app.state.hook';
import { useLoginState } from '@/state/hooks/login.state.hook';

import { CustomButton } from '../../CustomButton';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { useTranslations } from 'next-intl';
import { useQRCode } from 'next-qrcode';
import { isDesktop } from 'react-device-detect';
import {
  EPortalJourneyFlow,
  startPersistentPortalJourney,
} from '@/lib/portalJourney';

const REDIRECT_TIMEOUT = 10000;

export const OtpLogin = () => {
  const t = useTranslations();
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [deepLink, setDeepLink] = useState<string | null>(null);
  const { initialBoot, deviceType, platform, currentPageName } = useAppState();
  const { isLoggingIn, setIsLoggingIn, setLoginType, loginResponse, loginError, setLoginError, setIsOtpLoggingIn } = useLoginState();
  const { getOtpMutation } = useOtp();
  const { SVG } = useQRCode();

  const searchParams = useSearchParams();
  const pathname = usePathname();
  const proofKey = searchParams.get('proofKey');

  const theme = initialBoot?.theme;
  const qrCodeForegroundColor = getQrCodeForegroundColor(
    theme?.loginPopup?.qrCodeColor,
    theme?.general?.backgroundColor?.colorOne
  );
  const storeId = initialBoot?.stores?.[0];
  const isLoading = getOtpMutation.isPending || isLoggingIn;
  const isDeeplinkError = getOtpMutation.isError && !getOtpMutation.isPending;
  const otpTextModel = initialBoot?.integrations?.playersAuthentication?.otpTextModel;
  const otpOn = initialBoot?.integrations?.playersAuthentication?.otpOn;

  const numOfDigits = useMemo(() => {
    return otpTextModel === EOTPTextModel.SIX_DIGITS ? 6 : 4;
  }, [initialBoot?.integrations?.playersAuthentication?.otpTextModel]);

  useEffect(() => {
    if (!storeId || proofKey || otpOn === false) return;

    const utmParams = getUtmParams();

    const baseRequestData: OtpData = {
      publisherId: storeId,
      device: isDesktop ? EPlatform.DESKTOP : 'APPCHARGE',
      platform: platform?.toUpperCase() as EPlatformType
    };

    const requestData = {
      ...baseRequestData,
      ...(Object.keys(utmParams).length > 0 && {
        utm: {
          utmSource: utmParams.utm_source,
          utmMedium: utmParams.utm_medium,
          utmCampaign: utmParams.utm_campaign
        }
      }),
      metadata: {
        loginPageSource: currentPageName || 'Home'
      }
    };

    getOtpMutation.mutate(requestData as OtpData, {
      onSuccess: (response) => {
        setQrCode(response.deepLink);
        setAccessToken(response.accessToken);
        setDeepLink(response.deepLink);
        setLoginError(null);
      },
      onError: (error) => {
        console.error('Failed to get OTP deeplink:', error);
        setLoginError(t('portal.login.otpGenerateFailed'));
      }
    });
  }, [deviceType, platform, storeId, setLoginError, t, otpOn]);

  const login = (otpValue: string, accessToken?: string) => {
    setIsOtpLoggingIn(true);
    loginResponse({
      authMethod: ELoginMethods.OTP,
      otp: {
        playerCode: otpValue,
        accessToken: accessToken || ''
      }
    });
  };

  const handleAuthenticateMeBtnClick = useCallback(() => {
    startPersistentPortalJourney({
      flow: EPortalJourneyFlow.OTP_ROUND_TRIP,
      context: {
        authMethod: 'otp',
        pathname,
        source: 'game-deeplink',
      },
    });
    setIsLoggingIn(true);
    window.location.replace(deepLink ?? '');

    setTimeout(() => {
      setIsLoggingIn(false);
    }, REDIRECT_TIMEOUT);
  }, [deepLink, pathname, setIsLoggingIn]);

  if (isLoggingIn) {
    return <></>;
  }

  return (
    <div className='flex flex-col items-center justify-center gap-6'>
      {deviceType === EPlatform.DESKTOP ? (
        <>
          <h3 className='text-md text-center'>{t('portal.login.otpInstructions')}</h3>
          <div className='flex flex-col items-center justify-center'>
            {isLoading ? (
              <LoadingSpinner text={t('portal.login.generatingQrCode')} />
            ) : isDeeplinkError ? (
              <div className='text-center text-sm text-red-400'>
                {loginError || t('portal.login.otpGenerateFailed')}
              </div>
            ) : (
              <div className='flex flex-col items-center justify-center gap-6'>
                <SVG
                  text={qrCode ?? 'null'}
                  options={{
                    width: 150,
                    color: {
                      dark: qrCodeForegroundColor,
                      light: QR_CODE_BACKGROUND_COLOR
                    }
                  }}
                />

                <InputOTP
                  data-testid="PortalLoginModal-OtpInput"
                  className='border-slate-500'
                  style={{
                    marginTop: '20px'
                  }}
                  maxLength={numOfDigits}
                  onComplete={(value) => {
                    login(value, accessToken ?? undefined);
                  }}
                  onChange={() => {
                    if (loginError) {
                      setLoginError(null);
                    }
                  }}
                  disabled={isLoading}
                  autoFocus>
                  <InputOTPGroup>
                    {Array.from({ length: numOfDigits }, (_, i) => (
                      <InputOTPSlot
                        key={i}
                        index={i}
                        className={`${otpTextModel === EOTPTextModel.SIX_DIGITS ? 'size-14' : ''}`}
                      />
                    ))}
                  </InputOTPGroup>
                </InputOTP>

                {loginError && isDesktop && (
                  <div className='mt-2 text-center text-sm text-red-400'>
                    {t('portal.login.otpCodeIncorrect')}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      ) : isLoading ? (
        <LoadingSpinner />
      ) : isDeeplinkError ? (
        <div className='text-center text-sm text-red-400'>
          {loginError || t('portal.login.otpGenerateFailed')}
        </div>
      ) : (
        <div className='flex flex-col items-center gap-4'>
          <CustomButton style={getButtonStyle(theme)} onClick={handleAuthenticateMeBtnClick}>
            {t('portal.login.authenticateMeNow')}
          </CustomButton>
          {loginError && isDesktop && (
            <div className='text-center text-sm text-red-400'>
              {t('portal.login.otpCodeIncorrect')}
            </div>
          )}
        </div>
      )}
      <button
        className='text-center text-sm'
        style={{ color: theme?.loginPopup?.fontColor || '#FFF' }}
        onClick={() => {
          setLoginType(null);
          setIsLoggingIn(false);
          setLoginError(null);
        }}
        type='button'>
        {t('portal.login.back')}
      </button>
    </div>
  );
};
